import { useRef, useState, type KeyboardEvent } from "react";
import type { Book } from "@tebiki/shared";
import { books as booksApi } from "../lib/api.js";
import { Icon } from "./Icon.js";

/**
 * Tag-like 本 picker: every org book is a toggle chip; tapping attaches/detaches THIS manual
 * (POST/DELETE /api/books/:id/items — persisted immediately, like flipping a physical binder
 * open). "＋新しい本" creates a book inline (Notion-tag style) with this manual as its first
 * page. Controlled from outside via `books` + `onBooksChange` so several pickers stay in sync.
 */
export function BookPicker({
  manualId,
  books,
  onBooksChange,
  disabled,
}: {
  manualId: string;
  books: Book[];
  onBooksChange: (next: Book[]) => void;
  disabled?: boolean;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const newBookBtnRef = useRef<HTMLButtonElement>(null);

  const inBook = (b: Book) => b.manualIds.includes(manualId);

  /** Close the inline create form and put focus back on its trigger (never drop it on body). */
  function closeCreate() {
    setCreating(false);
    setDraft("");
    requestAnimationFrame(() => newBookBtnRef.current?.focus());
  }

  async function toggle(b: Book) {
    if (busyId || disabled) return;
    setBusyId(b.bookId);
    setError(null);
    try {
      const updated = inBook(b)
        ? await booksApi.removeItem(b.bookId, manualId)
        : await booksApi.addItem(b.bookId, manualId);
      onBooksChange(books.map((x) => (x.bookId === updated.bookId ? updated : x)));
    } catch {
      setError("本の更新に失敗しました。通信環境を確認してください。");
    } finally {
      setBusyId(null);
    }
  }

  async function createBook() {
    const title = draft.trim();
    if (!title || busyId || disabled) return;
    setBusyId("new");
    setError(null);
    try {
      const created = await booksApi.create({ title, manualIds: [manualId] });
      onBooksChange([created, ...books]);
      closeCreate();
    } catch {
      setError("本を作成できませんでした。通信環境を確認してください。");
    } finally {
      setBusyId(null);
    }
  }

  function onDraftKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      // stopPropagation: inside the 提出シート dialog these keys must act on the inline form
      // only — bubbling Escape would close the whole sheet and discard the user's edits.
      e.preventDefault();
      e.stopPropagation();
      void createBook();
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      closeCreate();
    }
  }

  return (
    <div>
      <div className="row" role="group" aria-label="この手順書を収める本" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {books.map((b) => {
          const on = inBook(b);
          return (
            <button
              key={b.bookId}
              type="button"
              aria-pressed={on}
              aria-label={`${b.title}（${b.manualIds.length}冊${b.mustRead ? "・必読" : ""}）${on ? "から外す" : "に入れる"}`}
              disabled={disabled || busyId === b.bookId}
              onClick={() => void toggle(b)}
              className="tag jp"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                minHeight: 40,
                padding: "8px 14px",
                cursor: "pointer",
                borderColor: on ? "var(--hi-deep)" : "var(--line-2)",
                background: on ? "color-mix(in srgb,var(--hi) 18%,transparent)" : "transparent",
                color: on ? "var(--ink)" : "var(--muted)",
                opacity: busyId === b.bookId ? 0.6 : 1,
              }}
            >
              <Icon name={on ? "check" : "library"} size={14} aria-hidden />
              {b.title}
              {b.mustRead ? <Icon name="star" size={12} aria-hidden /> : null}
              <span className="mono" aria-hidden="true" style={{ fontSize: 11, color: "var(--muted)" }}>{b.manualIds.length}</span>
            </button>
          );
        })}

        {creating ? (
          <span
            className="row"
            style={{ gap: 6, border: "1px dashed var(--line-2)", borderRadius: 999, padding: "4px 6px 4px 14px", minHeight: 40 }}
          >
            <input
              autoFocus
              className="jp"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onDraftKey}
              placeholder="新しい本のタイトル"
              aria-label="新しい本のタイトル"
              style={{ border: 0, background: "none", outline: "none", color: "var(--ink)", fontSize: 14, width: 170 }}
            />
            <button
              type="button"
              className="tag"
              onClick={() => void createBook()}
              disabled={!draft.trim() || busyId === "new"}
              style={{ cursor: "pointer" }}
            >
              {busyId === "new" ? "作成中…" : "作成"}
            </button>
            <button
              type="button"
              className="tag"
              aria-label="本の作成をやめる"
              onClick={closeCreate}
              style={{ cursor: "pointer", color: "var(--muted)" }}
            >
              <Icon name="close" size={13} />
            </button>
          </span>
        ) : (
          <button
            ref={newBookBtnRef}
            type="button"
            className="tag"
            onClick={() => setCreating(true)}
            disabled={disabled}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 40, padding: "8px 14px", cursor: "pointer", borderStyle: "dashed" }}
          >
            <Icon name="plus" size={14} aria-hidden /> 新しい本
          </button>
        )}
      </div>
      {books.length === 0 && !creating ? (
        <p className="jp" style={{ margin: "8px 0 0", fontSize: 12.5, color: "var(--muted)" }}>
          まだ本がありません。「＋新しい本」で作ると、この手順書が最初の1冊目に入ります。
        </p>
      ) : null}
      {error ? (
        <p className="jp" role="alert" style={{ margin: "8px 0 0", fontSize: 12.5, color: "var(--danger)" }}>
          {error}
        </p>
      ) : null}
    </div>
  );
}
